import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: "website" | "article";
  structuredData?: object | object[];
}

const DEFAULT_TITLE = "ManageClean OS";
const DEFAULT_DESCRIPTION =
  "The autonomous intelligence engine built specifically for janitorial and commercial cleaning operations.";
const DEFAULT_IMAGE = "https://manageclean.com/dashboard_hero.png";
export const BASE_URL = "https://os.manageclean.com";

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = [],
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const canonicalUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  // Handle both single object and array of structured data
  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULT_TITLE} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data),
          }}
        />
      ))}
    </Helmet>
  );
}

// Pre-built Schema.org structured data helpers
export const schemas = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ManageClean",
    url: BASE_URL,
    logo: `${BASE_URL}/logo.svg`,
    description: DEFAULT_DESCRIPTION,
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@manageclean.com",
      contactType: "customer support",
    },
    sameAs: [
      // Add social media URLs when available
    ],
  },

  webSite: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ManageClean",
    url: BASE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BASE_URL}/success-hub?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  },

  softwareApplication: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ManageClean OS",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description:
      "The all-in-one platform for janitorial business management. AI-powered tools for leads, bids, scheduling, and team management.",
    offers: {
      "@type": "AggregateOffer",
      lowPrice: "0",
      highPrice: "349",
      priceCurrency: "USD",
      offerCount: 6,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "120",
    },
    featureList: [
      "AI Business Assistant",
      "Client Management",
      "Bid & Proposal Generation",
      "Service Scheduling",
      "Staff Management",
      "Document Writer",
    ],
  },

  // Helper to create FAQ structured data
  createFAQSchema: (faqs: { question: string; answer: string }[]) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  }),

  // Helper to create Product schema for pricing
  createProductSchema: (plan: {
    name: string;
    description: string;
    price: number;
    currency?: string;
  }) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: `ManageClean ${plan.name}`,
    description: plan.description,
    brand: {
      "@type": "Brand",
      name: "ManageClean",
    },
    offers: {
      "@type": "Offer",
      price: plan.price.toString(),
      priceCurrency: plan.currency || "USD",
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      availability: "https://schema.org/InStock",
    },
  }),

  // Helper to create BreadcrumbList schema
  createBreadcrumbSchema: (items: { name: string; url: string }[]) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  }),

  // Helper to create Article / TechArticle schema
  createArticleSchema: (article: {
    headline: string;
    description?: string;
    datePublished?: string;
    dateModified?: string;
    authorName?: string;
    url: string;
    image?: string;
  }) => ({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: article.headline,
    description: article.description || article.headline,
    url: `${BASE_URL}${article.url}`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${BASE_URL}${article.url}`,
    },
    image: article.image || DEFAULT_IMAGE,
    datePublished: article.datePublished || "2024-03-30T00:00:00Z",
    dateModified: article.dateModified || new Date().toISOString(),
    author: {
      "@type": "Organization",
      name: article.authorName || "ManageClean",
      url: BASE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "ManageClean",
      url: BASE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/logo.svg`,
      },
    },
  }),

  // Helper to create HowTo schema for walkthroughs and guides
  createHowToSchema: (howTo: {
    name: string;
    description: string;
    steps: { name: string; text: string; url?: string }[];
  }) => ({
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: howTo.name,
    description: howTo.description,
    step: howTo.steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
      url: step.url ? `${BASE_URL}${step.url}` : undefined,
    })),
  }),

  // Helper to create Feature / SoftwareApplication module schema
  createFeatureModuleSchema: (feature: {
    name: string;
    description: string;
    category?: string;
    url: string;
    featureList: string[];
  }) => ({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: `ManageClean OS - ${feature.name}`,
    applicationCategory: feature.category || "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description: feature.description,
    url: `${BASE_URL}${feature.url}`,
    provider: {
      "@type": "Organization",
      name: "ManageClean",
      url: BASE_URL,
    },
    featureList: feature.featureList,
  }),
};

